import React, { useContext, useEffect, useState } from 'react';
import PhoneInput from '@/comp/form/phoneInput/PhoneInput';
import { useLocation, useNavigate } from 'react-router';
import Button, { ButtonProps } from '@/comp/button/button';
import './onSpotContact.scss';
import { PhoneInputFieldData } from '@/model/form/PhoneInput';
import { MailInput } from '@/comp/form/mailInput/MailInput';
import { SetValidMail } from '@/model/form/MailInput';
import BackButton from '@/comp/backButton/backButton';
import { OnSpotContext } from '../index';
import { OnSpotContextProps } from '@/model/onSpot/OnSpot';

interface OnSpotContactProps {
    contactData: { mail: string; confirmMail: string; mobile: PhoneInputFieldData };
    setContactData: (mail: string, confirmMail: string, mobile: PhoneInputFieldData) => void;
}

export const OnSpotContact = ({ contactData, setContactData }: OnSpotContactProps) => {
    const { routes } = useContext(OnSpotContext) as OnSpotContextProps;
    const [mobileNumberProps, setMobileNumberProps] = useState<PhoneInputFieldData>();
    const [formValid, setFormValid] = React.useState(false);
    const [mailProps, setMailProps] = React.useState({ mail: '', mailConfirm: '', valid: false });
    const [showError, setShowError] = React.useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    if (mailProps.mail === '' && contactData) {
        if (contactData.mail !== mailProps.mail || contactData.confirmMail !== mailProps.mailConfirm) {
            setMailProps({ ...mailProps, mail: contactData.mail, mailConfirm: contactData.confirmMail });
        }
        if (contactData.mobile.phoneNumber !== mobileNumberProps?.phoneNumber) {
            setMobileNumberProps({ ...contactData.mobile });
        }
    }

    useEffect(() => {
        const validateForm = () => {
            return !!(mobileNumberProps?.lengthValid && mailProps.valid);
        };

        const valid = validateForm();
        if (valid !== formValid) {
            setFormValid(valid);
        }
        if (showError) {
            setShowError(false);
        }
    }, [formValid, mobileNumberProps, mailProps.valid, showError]);

    const setPhoneNumber = (phoneNumber: PhoneInputFieldData) => {
        setMobileNumberProps(phoneNumber);
    };

    const setMail = (mail: SetValidMail) => {
        let changed = false;
        let newMailProps = { ...mailProps };
        if (mailProps.mail !== mail.value) {
            newMailProps.mail = mail.value;
            changed = true;
        }
        if (mailProps.mailConfirm !== mail.confirmValue) {
            newMailProps.mailConfirm = mail.confirmValue;
            changed = true;
        }
        if (mailProps.valid !== mail.valid) {
            newMailProps.valid = mail.valid;
            changed = true;
        }
        if (changed) {
            setMailProps({ ...newMailProps });
        }
    };

    const submitButtonProps = {
        type: 'submit',
        label: 'Weiter zur Zusammenfassung',
    } satisfies ButtonProps;

    const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const displayedQuestions = location.state?.displayedQuestions;
        if (formValid && mobileNumberProps) {
            setContactData(mailProps.mail, mailProps.mailConfirm, mobileNumberProps);
            navigate(`/${routes?.root}/${routes?.children?.review}`, {
                state: {
                    displayedQuestions: displayedQuestions,
                    treeSectionId: 'question-2',
                },
            });
        } else {
            setShowError(true);
        }
    };

    return (
        <>
            <div className="content-wrapper">
                <div className="intro row">
                    <h1 className="color-primary">Ihre Kontaktdaten</h1>
                    <div className="intro-text">
                        <p>
                            Damit wir Sie nach dem Eingang Ihres Anliegens direkt kontaktieren können, benötigen wir
                            Ihre Mobilnummer und E-Mail Adresse. Eine Eingangsbestätigung Ihrer Angaben wird an die von
                            Ihnen eingetragene E-Mail Adresse gesendet.
                        </p>
                    </div>
                </div>
            </div>
            <form className="section-contact-step">
                <section>
                    <article>
                        <MailInput
                            label={'E-Mail Adresse'}
                            emailValidation={true}
                            labelConfirm={'E-Mail Adresse bestätigen'}
                            emailValue={mailProps.mail}
                            emailConfirmValue={mailProps.mailConfirm}
                            setEmail={setMail}
                        />
                    </article>
                    <article>
                        <div className="container">
                            <PhoneInput
                                label="Mobilnummer"
                                setMobileNumber={setPhoneNumber}
                                withBorder={true}
                                phoneData={mobileNumberProps}
                            />
                            <div className="notice-text">
                                Kosten für Telefonate mit unserem Service-Team werden nach der Zusendung von
                                Verbindungsnachweisen durch uns selbstverständlich zurückerstattet.
                            </div>
                        </div>
                    </article>
                </section>
                <section className="section-submit">
                    <div className="container justify-between">
                        <BackButton
                            to={`/${routes?.root}/${routes?.children.upload}`}
                            label={'Zurück zu den Nachweisen'}
                        />
                        <Button
                            type={submitButtonProps.type}
                            label={submitButtonProps.label}
                            valid={formValid}
                            handleClick={handleSubmit}
                        />
                    </div>
                    <div className="container justify-center">
                        {showError && !formValid && (
                            <p className="error text-red">Bitte vervollständigen Sie erst Ihre Angaben.</p>
                        )}
                    </div>
                </section>
            </form>
        </>
    );
};
